/**
 * ColorWidget — compacte kleur-picker/converter voor het dashboard.
 * Invoer: HEX, RGB en CMYK (bewerkbaar). Uitvoer: HEX, RGB, CMYK en HSL (klik = kopie).
 * CMYK is eigen state (niet terug-afgeleid uit RGB) zodat bv. C100 M100 Y100 blijft.
 * Bij hoogte 'compact' een ingekorte versie (picker + hex + kopieer). Volledige tool
 * via de knop rechtsboven (WidgetShell).
 */
import React, { useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { HexColorPicker } from 'react-colorful';
import toast from 'react-hot-toast';
import { T, Icon } from './widgetTokens';
import {
  clampColor, rgbToHex, hexToRgb, rgbToHsl, rgbToCmyk, cmykToRgb, fmt,
} from '../../../utils/colorConverters';

const FALLBACK = { r: 99, g: 89, b: 131 };

const Label = ({ children }) => (
  <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.58rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: T.outline }}>{children}</Typography>
);

const numSx = {
  width: '100%', textAlign: 'center', bgcolor: T.surfaceLow, border: 'none', borderRadius: '0.4rem',
  px: 0.5, py: 0.75, color: T.text, outline: 'none', fontFamily: '"JetBrains Mono", monospace', fontSize: '0.78rem', fontWeight: 700,
  '&:focus': { boxShadow: `0 0 0 2px ${T.tertiary}40` },
  '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 },
  MozAppearance: 'textfield',
};

function Num({ value, max, onChange }) {
  return <Box component="input" type="number" min={0} max={max} value={value} onChange={(e) => onChange(e.target.value)} sx={numSx} />;
}

function HexField({ value, onChange }) {
  const [draft, setDraft] = useState(value);
  const [focused, setFocused] = useState(false);
  React.useEffect(() => { if (!focused) setDraft(value); }, [value, focused]);
  const commit = () => { if (hexToRgb(draft)) onChange(draft); else setDraft(value); };
  return (
    <Box component="input" value={draft} onFocus={() => setFocused(true)} onChange={(e) => setDraft(e.target.value)}
      onBlur={() => { setFocused(false); commit(); }} onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
      sx={{ ...numSx, textAlign: 'left', px: 1, py: 0.85, fontSize: '0.85rem' }} />
  );
}

export default function ColorWidget({ widget }) {
  const compact = widget?.h === 'compact';
  const [rgb, setRgb] = useState(FALLBACK);
  const [cmyk, setCmyk] = useState(() => rgbToCmyk(FALLBACK));
  const hex = useMemo(() => rgbToHex(rgb), [rgb]);
  const hsl = useMemo(() => rgbToHsl(rgb), [rgb]);

  const commitRgb = (next) => { setRgb(next); setCmyk(rgbToCmyk(next)); };
  const setHex = (v) => { const next = hexToRgb(v); if (next) commitRgb(next); };
  const setRgbField = (k, raw) => commitRgb({ ...rgb, [k]: clampColor(parseInt(raw, 10) || 0, 0, 255) });
  const setCmykField = (k, raw) => { const next = { ...cmyk, [k]: clampColor(parseInt(raw, 10) || 0, 0, 100) }; setCmyk(next); setRgb(cmykToRgb(next)); };

  const copy = (text, label) => navigator.clipboard?.writeText(text).then(
    () => toast.success(`${label} gekopieerd`), () => toast.error('Kopiëren mislukt'));

  const outputs = compact
    ? [['HEX', hex], ['RGB', fmt.rgb(rgb)]]
    : [['HEX', hex], ['RGB', fmt.rgb(rgb)], ['CMYK', fmt.cmyk(cmyk)], ['HSL', fmt.hsl(hsl)]];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: compact ? 1 : 1.25 }}>
      <Box sx={{
        '& .react-colorful': { width: '100%', height: compact ? 88 : 120 },
        '& .react-colorful__saturation': { borderRadius: '0.55rem 0.55rem 0 0' },
        '& .react-colorful__last-control': { borderRadius: '0 0 0.55rem 0.55rem' },
        '& .react-colorful__pointer': { width: 18, height: 18 },
      }}>
        <HexColorPicker color={hex.toLowerCase()} onChange={setHex} />
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ width: 30, height: 30, borderRadius: '0.4rem', bgcolor: hex, flexShrink: 0, border: `1px solid ${T.surfaceHigh}` }} />
        <Box sx={{ flex: 1 }}><HexField value={hex} onChange={setHex} /></Box>
      </Box>

      {!compact && (
        <>
          <Box>
            <Label>RGB</Label>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.5, mt: 0.25 }}>
              <Num value={rgb.r} max={255} onChange={(v) => setRgbField('r', v)} />
              <Num value={rgb.g} max={255} onChange={(v) => setRgbField('g', v)} />
              <Num value={rgb.b} max={255} onChange={(v) => setRgbField('b', v)} />
            </Box>
          </Box>
          <Box>
            <Label>CMYK</Label>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0.5, mt: 0.25 }}>
              <Num value={cmyk.c} max={100} onChange={(v) => setCmykField('c', v)} />
              <Num value={cmyk.m} max={100} onChange={(v) => setCmykField('m', v)} />
              <Num value={cmyk.y} max={100} onChange={(v) => setCmykField('y', v)} />
              <Num value={cmyk.k} max={100} onChange={(v) => setCmykField('k', v)} />
            </Box>
          </Box>
        </>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
        {outputs.map(([label, val]) => (
          <Box key={label} onClick={() => copy(val, label)}
            sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', bgcolor: T.surfaceLow, borderRadius: '0.4rem', px: 1, py: 0.6, '&:hover': { bgcolor: T.surfaceHigh } }}>
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.56rem', fontWeight: 800, letterSpacing: '0.06em', color: T.outline, width: 38, flexShrink: 0 }}>{label}</Typography>
            <Typography sx={{ flex: 1, fontFamily: '"JetBrains Mono", monospace', fontSize: '0.72rem', color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{val}</Typography>
            <Icon name="content_copy" size={12} sx={{ color: T.outlineVariant }} />
          </Box>
        ))}
      </Box>
    </Box>
  );
}
